import React, { useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  Pressable,
  TextInput,
  Image,
  Linking,
  ActivityIndicator,
  StyleSheet,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../contexts/ThemeContext';
import { useUser } from '../components/LoginPersistence';
import { useUserFunds } from '../hooks/useUserFunds';
import { formatFundsCents, MAIN_MUTUAL_FUND_ID } from '../constants/userFunds';
import MutualFundDonateWizardModal from '../components/funds/MutualFundDonateWizardModal';
import '../styles/fonts.css';
import { canvaHomePageBg, isAdminUser } from '../utils/hamburgerMenu';
import {
  MUTUAL_AID_COPY,
  mutualAidInfoLines,
  isVotingOpen,
  fundHeroImageSource,
  subscribeMutualAidPage,
  subscribeMutualAidSuggestions,
  submitMutualAidSuggestion,
  uploadDoneeImage,
  voteMutualAidSuggestion,
  unvoteMutualAidSuggestion,
  loadVotedSuggestionIds,
  remainingMutualAidVotes,
  MUTUAL_AID_VOTES_PER_USER,
  visibleActiveFund,
  visibleVoteSuggestions,
  visibleHomeFunds,
  fundPotProgress,
  suggestionDisplayFields,
} from '../utils/mutualAidFundService';

const ACCENT = '#3D7FE6';
const FUND_GOLD = '#e4c04a';
const DEFAULT_FUND_HERO = require('../../assets/backgrounds/mutual-aid-fund-hero.jpg');

function webHover(className) {
  return Platform.OS === 'web' ? { className } : {};
}

function Hoverable({
  as: Comp = View,
  style,
  hoverStyle,
  disabled,
  children,
  ...rest
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <Comp
      {...rest}
      disabled={disabled}
      onMouseEnter={(event) => {
        if (!disabled) setHovered(true);
        rest.onMouseEnter?.(event);
      }}
      onMouseLeave={(event) => {
        setHovered(false);
        rest.onMouseLeave?.(event);
      }}
      style={[styles.hoverBase, style, hovered && !disabled ? hoverStyle : null]}
    >
      {children}
    </Comp>
  );
}

function InfoTip({ lines, color, panelBg, borderColor }) {
  const [open, setOpen] = useState(false);
  return (
    <View style={styles.tipWrap}>
      <Hoverable
        as={Pressable}
        onPress={() => setOpen((prev) => !prev)}
        accessibilityRole="button"
        accessibilityLabel="How the Mutual Aid Fund works"
        style={[styles.tipBtn, { borderColor }]}
        hoverStyle={styles.btnHover}
        {...webHover('vrc-fund-btn')}
      >
        <Ionicons name="information-circle-outline" size={20} color={color} />
      </Hoverable>
      {open ? (
        <View style={[styles.tipPanel, { backgroundColor: panelBg, borderColor }]}>
          {lines.map((line) => (
            <Text key={line} style={[styles.tipLine, { color }]}>{line}</Text>
          ))}
        </View>
      ) : null}
    </View>
  );
}

export function MutualAidFund() {
  const { isDarkMode } = useTheme();
  const { user } = useUser();
  const funds = useUserFunds(user?.uid);
  const [view, setView] = useState('home');
  const [page, setPage] = useState({});
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsError, setSuggestionsError] = useState(null);
  const [votedIds, setVotedIds] = useState(new Set());
  const [previewVoteExtra, setPreviewVoteExtra] = useState({});
  const [busy, setBusy] = useState(false);
  const [donateBusy, setDonateBusy] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [flash, setFlash] = useState(null);
  const [form, setForm] = useState({
    name: '',
    title: '',
    link: '',
    contact: '',
    description: '',
    imageUrl: '',
  });

  const palette = useMemo(() => ({
    title: isDarkMode ? '#ffffff' : '#111111',
    body: isDarkMode ? 'rgba(255,255,255,0.82)' : 'rgba(0,0,0,0.72)',
    muted: isDarkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.45)',
    page: canvaHomePageBg(isDarkMode),
    inputBg: isDarkMode ? 'rgba(255,255,255,0.08)' : '#ffffff',
    inputText: isDarkMode ? '#ffffff' : '#111111',
    border: isDarkMode ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.12)',
    bar: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
    tipPanel: isDarkMode ? '#1a1c24' : '#ffffff',
  }), [isDarkMode]);

  const isAdmin = isAdminUser(user);
  const liveFund = funds.mutualFunds?.[0] || null;
  const { fund: activeFund } = visibleActiveFund(liveFund, isAdmin);
  const donateFund = useMemo(() => {
    const list = funds.mutualFunds || [];
    return (
      list.find((f) => f.isMain || f.id === MAIN_MUTUAL_FUND_ID) ||
      list.find((f) => f.id === activeFund?.id) ||
      list[0] ||
      activeFund ||
      null
    );
  }, [funds.mutualFunds, activeFund]);
  const heroSource = fundHeroImageSource(page, liveFund, DEFAULT_FUND_HERO);
  const infoLines = mutualAidInfoLines({ includeGuidelines: view === 'suggest' });
  const { rows: votable, preview: votePreview } = visibleVoteSuggestions({
    suggestions,
    page,
    isAdmin,
  });
  const votingOpen = isVotingOpen(page) || votePreview;
  const liveFunds = funds.mutualFunds || [];
  const { rows: homeFunds } = visibleHomeFunds({ funds: liveFunds, votable });
  const pot = fundPotProgress({ funds: liveFunds, isAdmin });
  const potPct = Math.min(100, Math.round((pot.raisedCents / Math.max(1, pot.goalCents)) * 100));

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search || '');
    if (params.get('donate') === 'success') {
      setFlash({ type: 'ok', text: 'Card donation received — raised amount updates when the payment clears.' });
    } else if (params.get('donate') === 'cancelled') {
      setFlash({ type: 'error', text: 'Card checkout cancelled. Any balance already applied stays donated.' });
    }
  }, []);

  useEffect(() => {
    return subscribeMutualAidPage(
      (data) => setPage(data || {}),
      () => setPage({}),
    );
  }, []);

  useEffect(() => {
    return subscribeMutualAidSuggestions(
      (rows) => {
        setSuggestionsError(null);
        setSuggestions(rows);
      },
      (err) => {
        setSuggestions([]);
        setSuggestionsError(err?.message || 'Could not load proposals.');
      },
    );
  }, []);

  useEffect(() => {
    if (!user?.uid) {
      setVotedIds(new Set());
      return;
    }
    if (suggestions.length === 0) return;
    let cancelled = false;
    loadVotedSuggestionIds(user.uid, suggestions.map((row) => row.id)).then((ids) => {
      if (!cancelled) setVotedIds(ids);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [user?.uid, suggestions]);

  const openDonateWizard = () => {
    setFlash(null);
    if (!user?.uid) {
      setFlash({ type: 'error', text: 'Sign in to donate.' });
      return;
    }
    if (!donateFund?.id) {
      setFlash({ type: 'error', text: 'Fund is not ready yet — try again in a moment.' });
      return;
    }
    setWizardOpen(true);
  };

  const handleWizardDonate = async ({ fundId, creditCents, cardCents }) => {
    setDonateBusy(true);
    setFlash(null);
    try {
      const origin =
        Platform.OS === 'web' && typeof window !== 'undefined' && window.location?.origin
          ? window.location.origin
          : null;
      const result = await funds.donateMixed(fundId, {
        creditCents,
        cardCents,
        ...(origin
          ? {
              successUrl: `${origin}/fund?donate=success`,
              cancelUrl: `${origin}/fund?donate=cancelled`,
            }
          : {}),
      });
      if (result?.url) {
        if (Platform.OS === 'web') {
          window.location.href = result.url;
          return;
        }
        await Linking.openURL(result.url);
        return;
      }
      setWizardOpen(false);
      setFlash({
        type: 'ok',
        text: `Donated ${formatFundsCents(creditCents || 0)} from your Wallet.`,
      });
    } catch (err) {
      console.error('Fund donate failed:', err);
      setFlash({ type: 'error', text: err?.message || 'Donation failed.' });
      throw err;
    } finally {
      setDonateBusy(false);
    }
  };

  const pickImage = () => {
    if (Platform.OS !== 'web') return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (event) => {
      const file = event.target.files?.[0];
      if (!file) return;
      try {
        setBusy(true);
        const url = await uploadDoneeImage(file);
        setForm((prev) => ({ ...prev, imageUrl: url }));
      } catch (err) {
        setFlash({ type: 'error', text: err?.message || 'Image upload failed.' });
      } finally {
        setBusy(false);
      }
    };
    input.click();
  };

  const sendSuggestion = async () => {
    try {
      setBusy(true);
      setFlash(null);
      await submitMutualAidSuggestion(form);
      setForm({ name: '', title: '', link: '', contact: '', description: '', imageUrl: '' });
      setFlash({ type: 'ok', text: 'Suggestion sent. An admin will review it before it appears in the vote.' });
      setView('home');
    } catch (err) {
      setFlash({ type: 'error', text: err?.message || 'Could not send suggestion.' });
    } finally {
      setBusy(false);
    }
  };

  const votesUsed = votedIds.size;
  const votesLeft = remainingMutualAidVotes(votesUsed);

  const vote = async (id) => {
    try {
      setBusy(true);
      setFlash(null);
      const already = votedIds.has(id);
      if (String(id || '').startsWith('preview-')) {
        if (already) {
          setVotedIds((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
          setPreviewVoteExtra((prev) => ({
            ...prev,
            [id]: Math.max(0, (prev[id] || 0) - 1),
          }));
          setFlash({ type: 'ok', text: 'Preview vote removed.' });
          return;
        }
        if (votesLeft <= 0) {
          setFlash({ type: 'error', text: `You already used all ${MUTUAL_AID_VOTES_PER_USER} votes. Unvote one to vote again.` });
          return;
        }
        setVotedIds((prev) => new Set([...prev, id]));
        setPreviewVoteExtra((prev) => ({ ...prev, [id]: (prev[id] || 0) + 1 }));
        setFlash({ type: 'ok', text: `Preview vote — ${remainingMutualAidVotes(votesUsed + 1)} of ${MUTUAL_AID_VOTES_PER_USER} left.` });
        return;
      }
      if (already) {
        await unvoteMutualAidSuggestion(id);
        setVotedIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        setFlash({ type: 'ok', text: 'Vote removed.' });
        return;
      }
      if (votesLeft <= 0) {
        setFlash({ type: 'error', text: `You already used all ${MUTUAL_AID_VOTES_PER_USER} votes. Unvote one to vote again.` });
        return;
      }
      await voteMutualAidSuggestion(id);
      setVotedIds((prev) => new Set([...prev, id]));
      setFlash({ type: 'ok', text: 'Vote recorded.' });
    } catch (err) {
      setFlash({ type: 'error', text: err?.message || 'Vote failed.' });
    } finally {
      setBusy(false);
    }
  };

  const pageTitle = view === 'suggest' ? 'Suggest a Donee' : MUTUAL_AID_COPY.title;

  const renderVoteTile = (row) => {
    const voted = votedIds.has(row.id);
    const votes = (row.voteCount || 0) + (previewVoteExtra[row.id] || 0);
    const card = suggestionDisplayFields(row);
    return (
      <Hoverable
        key={row.id}
        style={[styles.fundTile, { borderColor: palette.border, backgroundColor: 'rgba(17,19,24,0.88)' }]}
        hoverStyle={styles.tileHover}
        {...webHover('vrc-fund-tilt')}
      >
        {card.imageUrl ? (
          <Image source={{ uri: card.imageUrl }} style={styles.fundTileImg} />
        ) : (
          <View style={[styles.fundTileImg, { backgroundColor: palette.bar }]} />
        )}
        <Text style={[styles.fundTileTitle, { color: '#ffffff' }]}>{card.title}</Text>
        {card.name && card.name !== card.title ? (
          <Text style={[styles.proposalName, { color: 'rgba(255,255,255,0.82)' }]}>{card.name}</Text>
        ) : null}
        {card.description ? (
          <Text style={[styles.proposalDesc, { color: 'rgba(255,255,255,0.78)' }]}>{card.description}</Text>
        ) : null}
        {card.link ? (
          <Hoverable as={TouchableOpacity} onPress={() => Linking.openURL(card.link)} hoverStyle={styles.btnHover} {...webHover('vrc-fund-btn')}>
            <Text style={[styles.proposalMeta, { color: ACCENT }]}>{card.link}</Text>
          </Hoverable>
        ) : null}
        {card.contact ? (
          <Text style={[styles.proposalMeta, { color: 'rgba(255,255,255,0.55)' }]}>{card.contact}</Text>
        ) : null}
        <Text style={[styles.muted, { color: 'rgba(255,255,255,0.6)', marginTop: 6 }]}>
          {votes} vote{votes === 1 ? '' : 's'}
        </Text>
        <Hoverable
          as={TouchableOpacity}
          style={[styles.voteBtn, styles.tileVote, { backgroundColor: voted ? 'transparent' : ACCENT, borderColor: ACCENT, opacity: !voted && votesLeft <= 0 ? 0.45 : 1 }]}
          hoverStyle={styles.btnHover}
          onPress={() => vote(row.id)}
          disabled={busy || (!voted && votesLeft <= 0)}
          {...webHover('vrc-fund-btn')}
        >
          <Text style={{ color: voted ? ACCENT : '#fff', fontWeight: '700' }}>{voted ? 'Unvote' : 'Vote'}</Text>
        </Hoverable>
      </Hoverable>
    );
  };

  return (
    <View style={[styles.page, { backgroundColor: palette.page }]}>
      {view !== 'home' ? (
        <View style={styles.pageHeader}>
          <Hoverable as={TouchableOpacity} onPress={() => setView('home')} style={styles.inlineBack} hoverStyle={styles.btnHover} {...webHover('vrc-fund-btn')}>
            <Text style={[styles.backBtnText, { color: palette.title }]}>← Mutual Aid Fund</Text>
          </Hoverable>
          <View style={styles.titleRow}>
            <Text style={[styles.title, { color: palette.title }]}>{pageTitle}</Text>
            <InfoTip
              lines={infoLines}
              color={palette.body}
              panelBg={palette.tipPanel}
              borderColor={palette.border}
            />
          </View>
        </View>
      ) : null}

      {view === 'home' ? (
        <>
          <View pointerEvents="none" style={styles.photoStage}>
            {heroSource ? (
              <>
                <Image source={heroSource} style={styles.hero} resizeMode="cover" />
                <Image source={heroSource} style={[styles.hero, styles.heroBlur]} resizeMode="cover" />
              </>
            ) : null}
            <View style={styles.stageDim} />
            <View style={styles.stageTopScrim} />
            <View style={styles.stageScrim} />
          </View>
          <ScrollView style={styles.homeScroll} contentContainerStyle={styles.homeScrollInner}>
            <View style={styles.stageTop}>
              <View style={styles.titleRow}>
                <Text style={[styles.title, styles.heroTitle]}>{pageTitle}</Text>
                <InfoTip
                  lines={infoLines}
                  color="rgba(255,255,255,0.88)"
                  panelBg="rgba(17,19,24,0.94)"
                  borderColor="rgba(255,255,255,0.16)"
                />
              </View>
              <Text style={styles.heroSubtitle}>{MUTUAL_AID_COPY.subtitle}</Text>
            </View>
            <View style={styles.homeColumn}>
            <View style={styles.stageOverlay}>
              <Hoverable style={styles.actionDock} hoverStyle={styles.dockHover} {...webHover('vrc-fund-dock')}>
                <Text style={[styles.section, { color: FUND_GOLD }]}>
                  {pot.preview ? 'Admin preview' : 'Raised this cycle'}
                </Text>
                <Text style={styles.potRaised}>{formatFundsCents(pot.raisedCents)}</Text>
                <Text style={styles.potGoal}>of {formatFundsCents(pot.goalCents)} goal</Text>
                <View style={styles.potTrack}>
                  <View style={[styles.potFill, { width: `${potPct}%` }]} />
                </View>
                <View style={styles.stackBtns}>
                  <Hoverable as={TouchableOpacity} style={[styles.primaryBtn, styles.dockDonate, { backgroundColor: ACCENT }]} onPress={openDonateWizard} hoverStyle={styles.btnHover} {...webHover('vrc-fund-btn')}>
                    <Text style={styles.primaryBtnText}>DONATE TO FUND</Text>
                  </Hoverable>
                  <Hoverable
                    as={TouchableOpacity}
                    style={styles.ghostBtn}
                    onPress={() => { setFlash(null); setView('suggest'); }}
                    hoverStyle={styles.btnHover}
                    {...webHover('vrc-fund-btn')}
                  >
                    <Text style={[styles.secondaryBtnText, { color: '#ffffff' }]}>SUGGEST A DONEE</Text>
                  </Hoverable>
                </View>
                {flash ? (
                  <Text style={[styles.flash, { color: flash.type === 'error' ? '#ff8a80' : '#86efac' }]}>{flash.text}</Text>
                ) : null}
              </Hoverable>
            </View>
            <View style={styles.fundSection}>
              <Text style={styles.fundSectionTitle}>
                {votePreview ? 'Vote on this cycle' : 'This cycle’s funds'}
              </Text>
              {votingOpen && votable.length ? (
                <Text style={styles.votesLeft}>
                  {votesLeft} of {MUTUAL_AID_VOTES_PER_USER} votes left
                </Text>
              ) : null}
              {suggestionsError ? (
                <Text style={[styles.fundBlurb, { color: '#ff8a80' }]}>{suggestionsError}</Text>
              ) : votingOpen && votable.length ? (
                <View style={styles.fundGrid}>
                  {votable.map(renderVoteTile)}
                </View>
              ) : homeFunds.length ? (
                <View style={styles.fundGrid}>
                  {homeFunds.map((tile) => (
                    <Hoverable
                      key={tile.id}
                      style={[styles.fundTile, { borderColor: palette.border, backgroundColor: 'rgba(17,19,24,0.88)' }]}
                      hoverStyle={styles.tileHover}
                      {...webHover('vrc-fund-tilt')}
                    >
                      {tile.imageUrl ? (
                        <Image source={{ uri: tile.imageUrl }} style={styles.fundTileImg} />
                      ) : (
                        <View style={[styles.fundTileImg, { backgroundColor: palette.bar }]} />
                      )}
                      <Text style={[styles.fundTileTitle, { color: '#ffffff' }]}>{tile.title}</Text>
                      {tile.name && tile.name !== tile.title ? (
                        <Text style={[styles.fundTileName, { color: 'rgba(255,255,255,0.82)' }]}>{tile.name}</Text>
                      ) : null}
                      {tile.description ? (
                        <Text style={[styles.fundTileDesc, { color: 'rgba(255,255,255,0.78)' }]}>{tile.description}</Text>
                      ) : null}
                    </Hoverable>
                  ))}
                </View>
              ) : (
                <Text style={[styles.fundBlurb, { color: 'rgba(255,255,255,0.7)' }]}>
                  {votingOpen ? 'No approved donees in this vote yet.' : 'Voting is not open yet. Approved donees will show here during the voting period.'}
                </Text>
              )}
            </View>
            </View>
          </ScrollView>
        </>
      ) : (
        <ScrollView contentContainerStyle={styles.formScroll} keyboardShouldPersistTaps="handled">
          {view === 'suggest' ? (
            <>
              {[
                { key: 'name', label: 'Name', placeholder: 'Venue, artist, or person' },
                { key: 'title', label: 'Title', placeholder: 'What the funds are for' },
                { key: 'link', label: 'Link', placeholder: 'https://' },
                { key: 'contact', label: 'Contact', placeholder: 'Email or phone' },
              ].map((field) => (
                <View key={field.key} style={styles.field}>
                  <Text style={[styles.label, { color: palette.body }]}>{field.label}</Text>
                  <TextInput
                    value={form[field.key]}
                    onChangeText={(value) => setForm((prev) => ({ ...prev, [field.key]: value }))}
                    placeholder={field.placeholder}
                    placeholderTextColor={palette.muted}
                    style={[styles.input, { backgroundColor: palette.inputBg, color: palette.inputText, borderColor: palette.border }]}
                    {...webHover('vrc-fund-field')}
                  />
                </View>
              ))}
              <View style={styles.field}>
                <Text style={[styles.label, { color: palette.body }]}>Image</Text>
                <Hoverable as={TouchableOpacity} style={[styles.imageBtn, { borderColor: palette.border }]} onPress={pickImage} disabled={busy} hoverStyle={styles.btnHover} {...webHover('vrc-fund-btn')}>
                  <Text style={{ color: palette.body }}>{form.imageUrl ? 'Replace image' : 'Upload image'}</Text>
                </Hoverable>
                {form.imageUrl ? <Image source={{ uri: form.imageUrl }} style={styles.preview} /> : null}
              </View>
              <View style={styles.field}>
                <Text style={[styles.label, { color: palette.body }]}>Description</Text>
                <TextInput
                  value={form.description}
                  onChangeText={(value) => setForm((prev) => ({ ...prev, description: value }))}
                  placeholder="Why this donee?"
                  placeholderTextColor={palette.muted}
                  multiline
                  numberOfLines={4}
                  style={[styles.input, styles.textarea, { backgroundColor: palette.inputBg, color: palette.inputText, borderColor: palette.border }]}
                  {...webHover('vrc-fund-field')}
                />
              </View>
              <Hoverable
                as={TouchableOpacity}
                style={[styles.primaryBtn, { backgroundColor: ACCENT, opacity: busy ? 0.6 : 1 }]}
                onPress={sendSuggestion}
                disabled={busy}
                hoverStyle={styles.btnHover}
                {...webHover('vrc-fund-btn')}
              >
                {busy ? <ActivityIndicator color="#fff" /> : <Text style={styles.primaryBtnText}>SEND</Text>}
              </Hoverable>
            </>
          ) : null}

          {flash ? (
            <Text style={[styles.flash, { color: flash.type === 'error' ? '#e74c3c' : '#2ecc71' }]}>{flash.text}</Text>
          ) : null}
        </ScrollView>
      )}

      <MutualFundDonateWizardModal
        visible={wizardOpen}
        onClose={() => setWizardOpen(false)}
        fund={donateFund}
        availableCents={funds.donateAvailableCents ?? funds.availableCents}
        busy={donateBusy}
        isDarkMode={isDarkMode}
        onDonate={handleWizardDonate}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  hoverBase: {
    ...(Platform.OS === 'web' ? {
      transitionProperty: 'transform, box-shadow, filter',
      transitionDuration: '180ms',
      cursor: 'pointer',
    } : {}),
  },
  tileHover: {
    ...(Platform.OS === 'web' ? {
      transform: 'perspective(900px) rotateX(5deg) rotateY(-7deg) translateY(-12px) scale(1.03)',
      boxShadow: '0 26px 50px rgba(0,0,0,0.45)',
      zIndex: 6,
    } : { transform: [{ scale: 1.02 }] }),
  },
  btnHover: {
    ...(Platform.OS === 'web' ? {
      transform: 'translateY(-4px) scale(1.04)',
      boxShadow: '0 12px 24px rgba(61,127,230,0.38)',
      filter: 'brightness(1.08)',
    } : { transform: [{ scale: 1.03 }] }),
  },
  dockHover: {
    ...(Platform.OS === 'web' ? {
      transform: 'translateY(-6px) scale(1.015)',
      boxShadow: '0 22px 44px rgba(0,0,0,0.36)',
    } : {}),
  },
  votesLeft: {
    fontFamily: 'VastagoGrotesk, system-ui, sans-serif',
    fontSize: 14,
    textAlign: 'center',
    color: 'rgba(255,255,255,0.78)',
    marginBottom: 14,
  },
  page: {
    flex: 1,
    width: '100%',
    minHeight: '100%',
    position: 'relative',
  },
  pageHeader: {
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingTop: 20,
    paddingBottom: 12,
    zIndex: 2,
  },
  stageTop: {
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingTop: 8,
    paddingBottom: 8,
    zIndex: 3,
  },
  heroTitle: {
    color: '#F4F1EA',
    textShadowColor: 'rgba(0,0,0,0.45)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 8,
  },
  heroSubtitle: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 15,
    lineHeight: 21,
    textAlign: 'center',
    maxWidth: 520,
    marginTop: 8,
    color: 'rgba(244,241,234,0.88)',
    textShadowColor: 'rgba(0,0,0,0.4)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 6,
  },
  stageTopScrim: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    height: 180,
    ...(Platform.OS === 'web'
      ? { backgroundImage: 'linear-gradient(to bottom, rgba(0,0,0,0.62), transparent)' }
      : { backgroundColor: 'rgba(0,0,0,0.28)' }),
  },
  inlineBack: {
    alignSelf: 'flex-start',
    marginBottom: 8,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  subtitle: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 16,
    lineHeight: 22,
    textAlign: 'center',
    maxWidth: 560,
    marginTop: 8,
  },
  title: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 32,
    fontWeight: '800',
    letterSpacing: 1,
    textAlign: 'center',
  },
  backBtnText: { fontFamily: 'VastagoGrotesk', fontSize: 15, fontWeight: '600' },
  tipWrap: { position: 'relative' },
  tipBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tipPanel: {
    position: 'absolute',
    top: 40,
    left: 0,
    width: 280,
    borderRadius: 12,
    borderWidth: 1,
    padding: 14,
    zIndex: 8,
    ...(Platform.OS === 'web' ? { boxShadow: '0 8px 24px rgba(0,0,0,0.28)' } : {}),
  },
  tipLine: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 13,
    lineHeight: 19,
    marginBottom: 8,
  },
  homeScroll: { flex: 1, width: '100%', zIndex: 2 },
  homeScrollInner: { paddingBottom: 48, flexGrow: 1 },
  photoStage: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 0,
  },
  hero: {
    ...StyleSheet.absoluteFillObject,
    width: '100%',
    height: '100%',
  },
  heroBlur: {
    ...(Platform.OS === 'web'
      ? { filter: 'blur(16px)', transform: [{ scale: 1.08 }], opacity: 0.45 }
      : {}),
  },
  stageDim: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.28)',
    ...(Platform.OS === 'web'
      ? { backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)' }
      : {}),
  },
  stageScrim: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: '55%',
    ...(Platform.OS === 'web'
      ? { backgroundImage: 'linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.28) 55%, transparent 100%)' }
      : { backgroundColor: 'rgba(0,0,0,0.36)' }),
  },
  homeColumn: {
    width: '100%',
    maxWidth: 1120,
    alignSelf: 'center',
    paddingHorizontal: 20,
  },
  stageOverlay: {
    alignItems: 'stretch',
    paddingHorizontal: 0,
    paddingTop: 4,
    paddingBottom: 18,
    zIndex: 3,
  },
  actionDock: {
    width: '100%',
    maxWidth: '100%',
    minHeight: 280,
    borderRadius: 20,
    paddingHorizontal: 36,
    paddingVertical: 32,
    gap: 14,
    alignItems: 'stretch',
    justifyContent: 'center',
    backgroundColor: 'rgba(12,14,16,0.72)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
    ...(Platform.OS === 'web' ? { backdropFilter: 'blur(18px)', WebkitBackdropFilter: 'blur(18px)' } : {}),
  },
  primaryBtn: {
    borderRadius: 10,
    paddingVertical: 14,
    paddingHorizontal: 18,
    alignItems: 'center',
    minWidth: 240,
    width: '100%',
    maxWidth: 360,
  },
  dockDonate: {
    maxWidth: '100%',
    alignSelf: 'stretch',
  },
  primaryBtnText: {
    color: '#ffffff',
    fontFamily: 'VastagoGrotesk',
    fontSize: 15,
    fontWeight: '800',
    letterSpacing: 0.8,
  },
  section: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    fontWeight: '700',
    textAlign: 'center',
  },
  potRaised: {
    fontFamily: 'VastagoGrotesk, system-ui, sans-serif',
    fontSize: 64,
    fontWeight: '800',
    letterSpacing: 0.4,
    textAlign: 'center',
    color: '#ffffff',
    lineHeight: 70,
  },
  potGoal: {
    fontFamily: 'VastagoGrotesk, system-ui, sans-serif',
    fontSize: 18,
    fontWeight: '600',
    textAlign: 'center',
    color: 'rgba(255,255,255,0.82)',
  },
  potTrack: {
    width: '100%',
    height: 10,
    borderRadius: 999,
    backgroundColor: 'rgba(255,255,255,0.16)',
    overflow: 'hidden',
    marginBottom: 6,
  },
  potFill: {
    height: '100%',
    borderRadius: 999,
    backgroundColor: FUND_GOLD,
  },
  fundSection: {
    width: '100%',
    maxWidth: '100%',
    alignSelf: 'stretch',
    paddingHorizontal: 0,
    paddingTop: 8,
  },
  fundSectionTitle: {
    fontFamily: 'VastagoGrotesk, system-ui, sans-serif',
    fontSize: 20,
    fontWeight: '800',
    textAlign: 'center',
    marginBottom: 16,
    color: '#ffffff',
  },
  fundGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 16,
    overflow: 'visible',
    zIndex: 2,
  },
  fundTile: {
    minWidth: 300,
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    borderWidth: 1,
    borderRadius: 14,
    overflow: 'visible',
    paddingBottom: 14,
  },
  fundTileImg: {
    width: '100%',
    height: 150,
    backgroundColor: 'rgba(0,0,0,0.08)',
    marginBottom: 12,
    borderTopLeftRadius: 14,
    borderTopRightRadius: 14,
  },
  fundTileTitle: {
    fontFamily: 'VastagoGrotesk, system-ui, sans-serif',
    fontSize: 17,
    fontWeight: '700',
    textAlign: 'center',
    paddingHorizontal: 12,
  },
  fundTileName: {
    fontFamily: 'VastagoGrotesk, system-ui, sans-serif',
    fontSize: 14,
    textAlign: 'center',
    paddingHorizontal: 12,
    marginTop: 4,
  },
  fundTileDesc: {
    fontFamily: 'VastagoGrotesk, system-ui, sans-serif',
    fontSize: 14,
    lineHeight: 21,
    textAlign: 'center',
    paddingHorizontal: 14,
    marginTop: 8,
  },
  fundName: {
    fontFamily: 'VastagoGrotesk, system-ui, sans-serif',
    fontSize: 22,
    fontWeight: '700',
    textAlign: 'center',
  },
  fundBlurb: {
    fontFamily: 'VastagoGrotesk, system-ui, sans-serif',
    fontSize: 15,
    lineHeight: 22,
    textAlign: 'center',
    maxWidth: 520,
    alignSelf: 'center',
  },
  proposalDesc: {
    fontFamily: 'VastagoGrotesk, system-ui, sans-serif',
    fontSize: 16,
    lineHeight: 26,
    textAlign: 'center',
    alignSelf: 'center',
    width: '100%',
    maxWidth: 560,
    marginTop: 4,
    marginBottom: 14,
    paddingHorizontal: 8,
  },
  muted: { fontFamily: 'VastagoGrotesk', fontSize: 13, textAlign: 'center' },
  stackBtns: {
    width: '100%',
    gap: 10,
    alignItems: 'stretch',
  },
  secondaryRow: {
    flexDirection: 'row',
    gap: 8,
    width: '100%',
  },
  ghostBtn: {
    width: '100%',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.28)',
    borderRadius: 10,
    paddingVertical: 11,
    paddingHorizontal: 8,
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.06)',
  },
  secondaryBtn: {
    borderWidth: 1.5,
    borderRadius: 10,
    paddingVertical: 12,
    paddingHorizontal: 14,
    width: '100%',
    alignItems: 'center',
  },
  secondaryBtnText: { fontFamily: 'VastagoGrotesk', fontSize: 13, fontWeight: '800', letterSpacing: 0.4 },
  formScroll: {
    width: '100%',
    maxWidth: 760,
    alignSelf: 'center',
    padding: 28,
    paddingBottom: 48,
  },
  field: { marginBottom: 14 },
  label: { fontFamily: 'VastagoGrotesk', fontSize: 13, fontWeight: '700', marginBottom: 6 },
  input: {
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontFamily: 'VastagoGrotesk',
    fontSize: 15,
  },
  textarea: { minHeight: 100, textAlignVertical: 'top' },
  imageBtn: { borderWidth: 1, borderRadius: 8, padding: 12, alignItems: 'center' },
  preview: { width: '100%', height: 140, borderRadius: 8, marginTop: 10 },
  proposal: { borderWidth: 1, borderRadius: 12, padding: 18, marginBottom: 16, alignItems: 'center' },
  proposalImg: { width: '100%', height: 200, borderRadius: 8, marginBottom: 12, backgroundColor: 'rgba(0,0,0,0.06)', alignSelf: 'stretch' },
  proposalName: { fontFamily: 'VastagoGrotesk', fontSize: 15, fontWeight: '600', textAlign: 'center', marginBottom: 6 },
  proposalMeta: { fontFamily: 'VastagoGrotesk', fontSize: 13, textAlign: 'center', marginBottom: 4 },
  voteBtn: { marginTop: 10, borderWidth: 1.5, borderRadius: 8, paddingVertical: 10, alignItems: 'center', alignSelf: 'stretch', width: '100%' },
  tileVote: { marginHorizontal: 14, marginBottom: 4, width: 'auto' },
  flash: { marginTop: 4, textAlign: 'center', fontFamily: 'VastagoGrotesk', fontWeight: '600' },
});
